import React, { useEffect, useState } from "react"
import { useSnapshot } from "valtio"
import { SelectChangeEvent } from "@mui/material"
import { InlineChoice } from "./InlineChoice"
import { CurrentTestState } from "../../storage"
import {
  getFeedbackItems,
  getResponseSingleItem,
  useAssessmentItemResponseScoring,
  useReactiveItemResponse,
  useResponseUpdate,
  useStateReset
} from "../../helpers"
import { FIELD_SELECT_ATTRIBUTES } from "../../constants"
import {
  InteractionType,
  NeutralSetting,
  onSaveStudentAnswer,
  isQuestionComplete,
  CheckCorrect,
  Variant,
  ContainerType
} from "../../types"

interface InlineChoiceInteractionProps {
  itemId: string
  responseidentifier: string
  shuffle?: string
  required?: string
  children?: JSX.Element
  accessibilityAttr?: { [key: string]: string }
  onSaveStudentAnswer?: onSaveStudentAnswer
  isCompletedQuestion?: isQuestionComplete
  checkCorrect?: CheckCorrect
  isNeutralSetting?: NeutralSetting
  containerType?: ContainerType
}

export const InlineChoiceInteractionComponent: React.FC<
  InlineChoiceInteractionProps
> = props => {
  const {
    children,
    responseidentifier: responseIdentifier,
    accessibilityAttr,
    onSaveStudentAnswer,
    isCompletedQuestion,
    checkCorrect,
    isNeutralSetting,
    containerType
  } = props
  const { currentItemResponse, currentTestPart } = useSnapshot(CurrentTestState)
  const itemResponse = useReactiveItemResponse(responseIdentifier)
  const { correct, scoring, groupMode, groupStatistics, correctResponse } =
    useAssessmentItemResponseScoring(
      currentItemResponse?.itemId,
      responseIdentifier
    )

  const { questionCompleted, correctAnswers } = isCompletedQuestion?.(
    InteractionType.inlineChoice,
    responseIdentifier
  ) ?? {
    questionCompleted: false,
    correctAnswers: []
  }

  const feedbackItems = getFeedbackItems(
    React.Children.toArray(children).filter(
      (child: any) => child.props
    ) as JSX.Element[],
    groupStatistics,
    correctResponse
  )

  const [value, setValue] = useState<string>(null)

  useEffect(() => {
    if (groupMode ? !correctResponse : !itemResponse) return
    setValue(getResponseSingleItem(groupMode ? correctResponse : itemResponse))
  }, [itemResponse, groupMode, correctResponse])

  // Handles answer reveal for lessons
  useEffect(() => {
    // When question is completed and correct answers available, display the correct answer
    // Unless the setting is neutral
    if (questionCompleted && correctAnswers?.length > 0 && !isNeutralSetting) {
      setValue(correctAnswers[0])
    }
  }, [questionCompleted, correctAnswers, isNeutralSetting])

  useResponseUpdate(
    currentItemResponse?.id,
    responseIdentifier,
    value,
    InteractionType.inlineChoice,
    transformToArray
  )

  const handleChange = (value: string, event?: SelectChangeEvent<any>) => {
    if (currentTestPart?.isFinished) return

    setValue(value)
    onSaveStudentAnswer({
      interactionType: InteractionType.inlineChoice,
      answerId: value,
      responseIdentifier: responseIdentifier
    })
  }

  const badgeClass = (
    isNeutralSetting: NeutralSetting
  ): { variant: Variant | null } => {
    if (value && isNeutralSetting) {
      return { variant: Variant.neutral }
    } else if (value && checkCorrect) {
      return checkCorrect?.(value, responseIdentifier)
        ? { variant: Variant.correct }
        : { variant: Variant.invalid }
    }
    return { variant: null }
  }

  // Reset state on response ID change.
  useStateReset(
    currentItemResponse?.id,
    responseIdentifier,
    setValue,
    getResponseSingleItem
  )

  const { "aria-label": ariaLabel = null, ...restAccessibilityAttr } =
    accessibilityAttr || {}

  return (
    <InlineChoice
      value={value}
      containerType={containerType}
      onChange={handleChange}
      correct={correct} // for tests and lessons
      correctAnswer={scoring?.response?.[0]} // for tests only
      groupMode={groupMode}
      feedbackItems={feedbackItems}
      {...(value ? {} : { "aria-label": ariaLabel || "Collapsed menu" })}
      SelectDisplayProps={{
        ...FIELD_SELECT_ATTRIBUTES,
        ...restAccessibilityAttr
      }}
      badgeClass={{ ...badgeClass(isNeutralSetting) }}
      questionCompleted={questionCompleted}
    >
      {children}
    </InlineChoice>
  )
}

const transformToArray = (value: string) => [value]
